export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString();
};

export const formatUTCTime = (utcTimestamp: string) => {
  const date = new Date(utcTimestamp);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
};

// Format Unix timestamp (seconds or milliseconds) to UTC time string
export const formatUnixTimestampToUTC = (unixTimestamp: number): string => {
  // Check if timestamp is in seconds (10 digits) or milliseconds (13 digits)
  // Convert to milliseconds if needed
  const timestampMs = unixTimestamp.toString().length <= 10 ? unixTimestamp * 1000 : unixTimestamp;

  const date = new Date(timestampMs);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
};

// Format Unix timestamp (seconds or milliseconds) to local time string
export const formatUnixTimestampToLocal = (unixTimestamp: number): string => {
  // Check if timestamp is in seconds (10 digits) or milliseconds (13 digits)
  // Convert to milliseconds if needed
  const timestampMs = unixTimestamp.toString().length <= 10 ? unixTimestamp * 1000 : unixTimestamp;

  const date = new Date(timestampMs);

  // Get local time components
  const localYear = date.getFullYear();
  const localMonth = String(date.getMonth() + 1).padStart(2, '0');
  const localDay = String(date.getDate()).padStart(2, '0');
  const localHours = String(date.getHours()).padStart(2, '0');
  const localMinutes = String(date.getMinutes()).padStart(2, '0');
  const localSeconds = String(date.getSeconds()).padStart(2, '0');

  // Get timezone abbreviation using Intl.DateTimeFormat
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneAbbr =
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value || 'Local';

  return `${localYear}-${localMonth}-${localDay} ${localHours}:${localMinutes}:${localSeconds} ${timeZoneAbbr}`;
};

export const convertToLocalTime = (utcTimestamp: string): string => {
  // Create date object from UTC timestamp
  // Only add 'Z' if it's not already present
  const dateString = utcTimestamp.endsWith('Z') ? utcTimestamp : utcTimestamp + 'Z';
  const date = new Date(dateString);

  // Get local time components
  const localYear = date.getFullYear();
  const localMonth = String(date.getMonth() + 1).padStart(2, '0');
  const localDay = String(date.getDate()).padStart(2, '0');
  const localHours = String(date.getHours()).padStart(2, '0');
  const localMinutes = String(date.getMinutes()).padStart(2, '0');

  // Get timezone abbreviation using Intl.DateTimeFormat
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneAbbr =
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value || 'UTC';

  return `${localYear}-${localMonth}-${localDay} ${localHours}:${localMinutes} ${timeZoneAbbr}`;
};

export const convertToLocalDate = (utcTimestamp: string): string => {
  // Create date object from UTC timestamp
  // Only add 'Z' if it's not already present
  const dateString = utcTimestamp.endsWith('Z') ? utcTimestamp : utcTimestamp + 'Z';
  const date = new Date(dateString);

  // Get local date components
  const localYear = date.getFullYear();
  const localMonth = String(date.getMonth() + 1).padStart(2, '0');
  const localDay = String(date.getDate()).padStart(2, '0');

  return `${localYear}-${localMonth}-${localDay}`;
};

// Format date to YYYY-MM-DD
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format ISO date string to YYYY-MM-DD HH:mm (UTC)
export const formatISODateTimeToUTC = (isoDateString: string | undefined): string => {
  if (!isoDateString) {
    return '';
  }

  const date = new Date(isoDateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes} (UTC)`;
};

// Get months between two dates (inclusive)
export const getMonthsBetweenDates = (startDate: string, endDate: string): string[] => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const months: string[] = [];
  const currentDate = new Date(start);

  // Set to first day of month to ensure proper month counting
  currentDate.setDate(1);
  end.setDate(1);

  while (currentDate <= end) {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);

    // Move to next month
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return months;
};

// Get number of months between two dates (inclusive)
export const getNumberOfMonths = (startDate: string, endDate: string): number => {
  const months = getMonthsBetweenDates(startDate, endDate);
  return months.length;
};

// Get default date range for orders (from 2 months ago to end of current month)
export const getDefaultOrderDateRange = (): { startDate: string; endDate: string } => {
  const today = new Date();

  // Start date: First day of month from 2 months ago
  const startDate = new Date(today);
  startDate.setMonth(today.getMonth() - 2);
  startDate.setDate(1);

  // End date: Last day of current month
  const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  return {
    startDate: formatDateToYYYYMMDD(startDate),
    endDate: formatDateToYYYYMMDD(endDate),
  };
};

// Format ISO datetime string to a user-friendly format
export const formatDateTime = (isoDateString: string): string => {
  try {
    const date = new Date(isoDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return isoDateString; // Return original string if parsing fails
  }
};
